/**
 * Homepage — the revenue numbers, as a section.
 *
 * `caseStudies` in lib/brandData has six engagements with closed-revenue
 * figures attached, and until now the homepage surfaced exactly one of them:
 * the 3.5× inside the method pyramid. The rest of the page argues for revenue
 * over rankings without ever showing the revenue, so this section does.
 *
 * Everything rendered here comes out of brandData — metric, label, industry,
 * HQ, timeframe and the `detail` line are all fields, never restated prose.
 * That is deliberate: the figures are client-supplied, and a number retyped
 * into a component is a number that can drift away from its source.
 *
 * Sits after GscProof. That module shows the Search Console curve turning;
 * this one says what the curve was worth. Order matters — the chart earns the
 * number.
 *
 * Responsive behavior is CSS media queries rather than a useIsMobile branch,
 * because scripts/prerender.mjs renders the homepage at a 390px viewport with
 * isMobile: true and captures the DOM after effects run; an inline style would
 * bake the mobile layout in and reflow on hydration. Same reason the reveal is
 * a one-shot animation and not a persistent hidden state.
 */

import React, { useEffect, useRef } from "react";
import { caseStudies } from "@/lib/brandData";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";

/** The three that lead, and the three that get a one-line mention below. */
const FEATURED_IDS = ["solar-group", "branding-studio", "storage-company"];
const SECONDARY_IDS = ["fintech-saas", "biotech-clinic", "investor-saas"];

const byId = (id: string) => caseStudies.find((c) => c.id === id);

const FEATURED = FEATURED_IDS.map(byId).filter(Boolean) as typeof caseStudies;
const SECONDARY = SECONDARY_IDS.map(byId).filter(Boolean) as typeof caseStudies;

export default function CaseStudyProof() {
  const root = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = root.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const targets = el.querySelectorAll<HTMLElement>("[data-reveal]");
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add("csp-in");
          io.unobserve(e.target);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    );
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={root}
      className="csp"
      aria-labelledby="csp-heading"
      style={{ background: "#0a0a0a", position: "relative", overflow: "hidden", fontFamily: ff }}
    >
      <div
        className="glow-blob"
        aria-hidden="true"
        style={{ left: "-180px", bottom: "-200px", width: "520px", height: "520px", opacity: 0.09 }}
      />

      <div className="csp-inner">
        <div className="csp-head" data-reveal>
          <div>
            <span className="csp-eyebrow">Proof, in the only unit that counts</span>
            <h2 id="csp-heading" className="csp-h2">
              Search that turned<br />
              <span style={{ color: "var(--cyan)" }}>into money.</span>
            </h2>
            <p className="csp-sub">
              Not rankings. Not traffic. Closed revenue, tracked against pipeline, on real engagements.
            </p>
          </div>
          <a href="/client-stories" className="csp-all">See all case studies →</a>
        </div>

        <div className="csp-grid" data-reveal>
          {FEATURED.map((c, i) => (
            <article key={c.id} className={`csp-card${i === 0 ? " csp-card-lead" : ""}`}>
              <div className="csp-metric">{c.metric}</div>
              <div className="csp-metric-label">{c.metricLabel}</div>
              <div className="csp-second">
                <strong>{c.secondaryMetric}</strong> {c.secondaryLabel.toLowerCase()}
              </div>
              <p className="csp-body">{c.detail}</p>
              <div className="csp-spacer" />
              {/* Industry and HQ stack rather than sharing a line: "Branding and
                  Design Services · United States" wraps at card width, which
                  pushed that card's rule out of line with its neighbors. */}
              <div className="csp-foot">
                <div className="csp-foot-l">
                  <span className="csp-meta">{c.industry}</span>
                  <span className="csp-hq">{c.hq}</span>
                </div>
                <span className="csp-time">{c.timeframe}</span>
              </div>
            </article>
          ))}
        </div>

        <div className="csp-strip" data-reveal>
          {SECONDARY.map((c) => (
            <span key={c.id} className="csp-strip-item">
              <strong>{c.metric}</strong> {c.industry}
            </span>
          ))}
        </div>
      </div>

      <style>{`
        .csp { padding: 96px 72px; }
        .csp-inner { max-width: 1136px; margin: 0 auto; position: relative; }

        .csp-head {
          display: flex; align-items: flex-end; justify-content: space-between;
          gap: 48px; margin-bottom: 44px;
        }
        .csp-head > div { max-width: 680px; }
        .csp-eyebrow {
          display: block; margin-bottom: 18px; color: var(--cyan);
          font-size: 11px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase;
        }
        .csp-h2 {
          margin: 0; color: #fff; font-size: 46px; font-weight: 700;
          line-height: 1.08; letter-spacing: -0.03em;
        }
        .csp-sub { margin: 18px 0 0; color: var(--text-muted); font-size: 17.5px; line-height: 1.6; }
        .csp-all {
          flex-shrink: 0; color: var(--cyan); font-size: 16px; font-weight: 700;
          text-decoration: none; transition: opacity 0.2s ease;
        }
        .csp-all:hover { opacity: 0.7; }

        .csp-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }

        .csp-card {
          display: flex; flex-direction: column; box-sizing: border-box; min-height: 300px;
          padding: 28px 26px 26px;
          border: 1px solid var(--border-glass); border-radius: 20px;
          background: var(--fill-glass);
          transition: border-color 0.2s ease;
        }
        .csp-card-lead { border-color: rgba(87,215,226,0.38); background: rgba(87,215,226,0.06); }
        .csp-card:hover { border-color: rgba(87,215,226,0.45); }

        .csp-metric {
          color: var(--cyan); font-size: 54px; font-weight: 700;
          line-height: 1; letter-spacing: -0.04em;
        }
        .csp-metric-label {
          margin-top: 9px; color: rgba(255,255,255,0.75);
          font-size: 14px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
        }
        .csp-second {
          margin-top: 14px; padding-top: 14px;
          border-top: 1px solid rgba(255,255,255,0.07);
          color: rgba(255,255,255,0.5); font-size: 14px; line-height: 1.4;
        }
        .csp-second strong { color: var(--cyan); font-weight: 700; }
        .csp-body { margin: 14px 0 0; color: var(--text-muted); font-size: 15px; line-height: 1.6; }
        .csp-spacer { flex-grow: 1; min-height: 18px; }
        .csp-foot {
          display: flex; align-items: flex-end; justify-content: space-between; gap: 12px;
          padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.08);
        }
        .csp-foot-l { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
        .csp-meta { color: rgba(255,255,255,0.5); font-size: 12.5px; font-weight: 600; }
        .csp-hq { color: rgba(255,255,255,0.32); font-size: 12.5px; font-weight: 600; }
        .csp-time { flex-shrink: 0; color: rgba(255,255,255,0.35); font-size: 12.5px; font-weight: 700; }

        .csp-strip {
          display: flex; align-items: center; justify-content: center; flex-wrap: wrap;
          margin-top: 30px; padding-top: 26px; border-top: 1px solid rgba(255,255,255,0.08);
        }
        .csp-strip-item {
          padding: 0 26px; color: rgba(255,255,255,0.5); font-size: 15px;
          border-left: 1px solid rgba(255,255,255,0.12);
        }
        .csp-strip-item:first-child { border-left: none; }
        .csp-strip-item strong { color: #fff; font-weight: 700; }

        @media (max-width: 1023px) {
          .csp { padding: 60px 20px; }
          .csp-head { flex-direction: column; align-items: flex-start; gap: 20px; margin-bottom: 32px; }
          .csp-h2 { font-size: 32px; }
          .csp-sub { font-size: 16px; }
          .csp-grid { grid-template-columns: 1fr; gap: 14px; }
          .csp-card { min-height: 0; padding: 24px 22px 22px; }
          .csp-metric { font-size: 44px; }
          .csp-strip { flex-direction: column; align-items: flex-start; gap: 10px; }
          .csp-strip-item { padding: 0; border-left: none; }
        }

        /* Reveal — see the file header for why this is an animation and not a
           persistent hidden state. */
        @keyframes cspRise {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: none; }
        }
        .csp [data-reveal].csp-in {
          animation: cspRise 0.8s cubic-bezier(0.2, 0.6, 0.2, 1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .csp [data-reveal].csp-in { animation: none; }
        }
      `}</style>
    </section>
  );
}
